import { Store } from '../../../../../_ui-core_/base/package/patterns/package/store';
import { localStorage_getOrNull } from '../../../../../_ui-core_/base/package/util/localStorage';
import { tick } from '../../../../../_ui-core_/base/package/util/render';
import { LOCAL_STORAGE_KEY_RENDERSERVICE_DEBUGGER } from '../../../../../constants';

/**
 * invisible - render page invisible
 * visible - show rendered content or render page visible
 */
export const RenderState = {
  INVISIBLE: 'invisible',
  VISIBLE: 'visible',
} as const;

export type RenderState = (typeof RenderState)[keyof typeof RenderState];

export const TASK_TIMEOUT = 3_000;

export type SpriteSheetState = {
  ready: boolean;
};

export type Task = { id: number; name: string };

export default class RenderService {
  private _loginTaskId: number | null = null;
  private _pageTemplateTaskId: number | null = null;
  private _nextTaskId = 0;
  private _renderServiceCycles = 0;
  private _shouldShowInfo = false;
  private _tasks: Task[] = [];
  private readonly _clearTaskAfterMs = TASK_TIMEOUT; // Keep in sync with feature service timeout!
  private readonly _renderStore: Store<RenderState>;
  private readonly _spriteSheetReadyStore: Store<SpriteSheetState>;

  constructor() {
    this._renderStore = new Store<RenderState>(RenderState.INVISIBLE);
    this._spriteSheetReadyStore = new Store<SpriteSheetState>({ ready: false });
    this._shouldShowInfo = !!localStorage_getOrNull(LOCAL_STORAGE_KEY_RENDERSERVICE_DEBUGGER);

    // Add initial tasks
    this._loginTaskId = this.addTask('login');
    this._pageTemplateTaskId = this.addTask('page-template');
  }

  public get renderStore() {
    return this._renderStore;
  }

  public get spriteSheetStore() {
    return this._spriteSheetReadyStore;
  }

  public addTask(name: string): number {
    const id = this._nextTaskId++;
    window.$app.logger.log(`add task: '${name}' (id: ${id})`);
    // Check if there is already a task with the same name
    if (this._tasks.some((_) => _.name === name)) {
      window.$app.logger.log(`task with the same name already exists: '${name}' (id: ${id})`);
    }
    this._tasks.push({ id, name });

    // Render page invisible until all tasks are done.
    if (this._renderStore.value !== RenderState.INVISIBLE) {
      window.$app.logger.log(`state: '${RenderState.INVISIBLE}'`);
      this._renderStore.next(RenderState.INVISIBLE);
    }

    // Schedule task to be removed after a certain time if it's not removed before.
    setTimeout(() => {
      if (this._tasks.some((_) => _.id === id)) {
        window.$app.logger.log(`task timed out: '${name}' (id: ${id})`);
        this._paintDot(3);
        this.removeTask(id);
      }
    }, this._clearTaskAfterMs);

    return id;
  }

  /**
   * Remove a task by its ID
   * The return value is meant to be used to reset the task ID variable to null.
   * Example:
   * ```ts
   * this._myTaskId = this._renderService.removeTask(this._myTaskId);
   * ```
   * @param id - Task ID to remove
   * @returns null - Always returns null to allow resetting the task ID variable
   */
  public removeTask(id: number): null {
    const taskName = this._tasks.find((_) => _.id === id)?.name ?? 'unknown';
    this._tasks = this._tasks.filter((item) => item.id !== id);
    const remaining = this._tasks.length ? this._tasks.map((_) => _.name) : [];
    window.$app.logger.log(
      `remove task id: ${id}, name: '${taskName}' (${remaining.length} remaining: ${remaining.length ? `${remaining.join(', ')}` : '✓'})`,
    );
    if (taskName === 'unknown') {
      window.$app.logger.log('remove task failed');
    }
    this._checkTasks();
    return null;
  }

  public removeLoginTask(): void {
    tick().then(() => {
      if (this._loginTaskId !== null) {
        this._loginTaskId = this.removeTask(this._loginTaskId);
      }
    });
  }

  public removePageTemplateTask(): void {
    tick().then(() => {
      if (this._pageTemplateTaskId !== null) {
        this._pageTemplateTaskId = this.removeTask(this._pageTemplateTaskId);
      }
    });
  }

  private _checkTasks(): void {
    if (this._tasks.length !== 0) {
      return;
    }
    window.$app.logger.log(`no tasks remaining, render page state: '${RenderState.VISIBLE}'`);
    this._paintDot(0);
    tick().then(() => {
      if (this._renderStore.value !== RenderState.VISIBLE) {
        this._paintDot(1); // expected state change
      } else {
        this._paintDot(2); // unexpected state change
      }
      this._renderServiceCycles++;
      if (this._renderStore.value !== RenderState.VISIBLE) {
        window.$app.logger.log(`set visíble state: '${RenderState.VISIBLE}'`);
        this._renderStore.next(RenderState.VISIBLE);
        window.$app.perf.setTag('renderServiceVisible');
      }
    });
  }

  /**
   * Paint dot in the viewport corner in dev mode to visualize that the page is visible.
   */
  private _paintDot(type: number): void {
    if (!this._shouldShowInfo) {
      return;
    }
    const colors = ['darkcyan', 'limegreen', 'tomato', 'red'];
    const width = 4;
    const height = type < 3 ? 2 : 4;
    const dot = document.createElement('div');
    dot.style.position = 'fixed';
    dot.style.bottom = `${type * 3}px`;
    dot.style.right = `${this._renderServiceCycles * 6 + 4}px`;
    dot.style.width = `${width}px`;
    dot.style.height = `${height}px`;
    dot.style.backgroundColor = colors[type]!;
    dot.style.borderRadius = '2px';
    dot.style.zIndex = '10000';
    document.body.appendChild(dot);
  }
}
